import { useState, useEffect, useCallback, useRef } from "react";
import {
  Paper,
  Stack,
  Box,
  Tooltip,
  CircularProgress,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import { useParams } from "react-router";
import { useOutletContext, useNavigate } from "react-router-dom";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";

import EnhancedTable from "components/utils/Table.js";
import FormUtils from "utils/FormUtils.js";
import ButtonDropdown from "components/utils/ButtonDropdown.js";
import PopUpModal from "components/utils/PopUpModal.js";
import DateUtils from "utils/DateUtils.js";
import TableRowActionsCell from "components/utils/TableRowActionsCell.js";
import {
  DOCTOR_ASSESSMENT_FORM_TYPES,
  DOCTOR_STIM_FORM_TYPES,
} from "utils/constants/DoctorAssessmentFormTypes.js";
import { openDoctorAssessmentForm } from "utils/DoctorAssessmentFormUrls.js";

import AuditFieldsModal from "components/modals/AuditFieldsModal.js";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import StimFormReport from "./StimFormReport";
import AssessmentFormReport from "./AssessmentFormReport";

import { useSnackbar } from "notistack";
import useAPI from "hooks/useAPI.js";
import DoctorFormService from "services/DoctorFormService.js";
import PatientService from "services/PatientService";
import SnackbarConstants from "utils/constants/Snackbar.js";
import moment from "moment";

function Assessments() {
  const [hospitalId] = useOutletContext();
  const { patientId } = useParams();
  const [formsList, setFormsList] = useState([]);
  const [activeTab, setActiveTab] = useState("ASSESSMENT");
  const [popUpModalContent, setPopUpModalContent] = useState({
    createdAt: "",
    lastUpdatedAt: "",
    createdBy: "",
    lastUpdatedBy: "",
  });
  const [stimReportData, setStimReportData] = useState({});
  const [assessmentReportData, setAssessmentReportData] = useState({});
  const [assessmentReportLoading, setAssessmentReportLoading] = useState(false);
  const [notifyingFormId, setNotifyingFormId] = useState(null);

  const { enqueueSnackbar } = useSnackbar();

  const deleteAssessmentModalRef = useRef(null);
  const [deletingAssessmentId, setDeletingAssessmentId] = useState("");

  let navigate = useNavigate();
  const popUpModalRef = useRef(null);
  const stimReportPopUpModalRef = useRef(null);
  const assessmentReportModalRef = useRef(null);

  const getFormsAPI = useAPI(
    DoctorFormService.getAssessmentForms,
    getFormsHandler
  );
  const addAssessmentFormAPI = useAPI(
    DoctorFormService.addAssessmentForm,
    addAssessmentFormHandler
  );

  const deleteAssessmentFormAPI = useAPI(
    DoctorFormService.deleteAssessmentForm,
    deleteAssessmentFormHandler,
    true
  );

  const revokeAssessmentFormAPISubmission = useAPI(
    DoctorFormService.revokeAssessmentFormSubmission,
    revokeAssessmentFormSubmissionHandler,
    true
  );

  const createStimFromAssessmentAPI = useAPI(
    DoctorFormService.createStimFormFromAssessment,
    createStimFromAssessmentHandler,
    true
  );

  const getForms = useCallback(() => {
    getFormsAPI.execute(hospitalId, patientId);
  }, [hospitalId, patientId, getFormsAPI.execute]);

  useEffect(() => {
    if (hospitalId && patientId) {
      getFormsAPI.execute(hospitalId, patientId);
    }
  }, [hospitalId, patientId, getFormsAPI.execute]);

  function addAssessmentForm(formType) {
    addAssessmentFormAPI.execute(hospitalId, patientId, {
      doctorAssessmentFormType: formType,
    });
  }

  function deleteAssessmentForm(formId) {
    deleteAssessmentFormAPI.execute(hospitalId, patientId, formId);
  }

  function revokeAssessmentFormSubmission(formId) {
    revokeAssessmentFormAPISubmission.execute(hospitalId, patientId, formId);
  }

  function createStimFromAssessment(assessmentFormId) {
    createStimFromAssessmentAPI.execute(hospitalId, patientId, assessmentFormId);
  }

  function getStimFormReportData(formId) {
    PatientService.getPatient([hospitalId, patientId])
      .then(async (patientRes) => {
        const patientDOB = patientRes.data?.dob || null;

        const reportRes = await DoctorFormService.getStimFormReportData([hospitalId, patientId, formId]);

        const combinedData = {
          id: formId,
          ...reportRes.data,
          dob: patientDOB,
        };
        setStimReportData(combinedData);
        stimReportPopUpModalRef.current.openModal();
      });
  }

  function downloadAssessmentFormAsPdf(form) {
    openDoctorAssessmentForm({
      formTypeEnum: form.assessmentFormTypeEnum,
      hospitalId,
      patientId,
      assessmentFormId: form.id,
      isMigrated: form.isMigrated,
      viewMode: "read",
      downloadPdf: true,
    });
  }

  async function notifyPatientAboutAssessment(formId) {
    try {
      setNotifyingFormId(formId);
      await DoctorFormService.emailAssessmentReport([
        hospitalId,
        patientId,
        formId,
      ]);
      enqueueSnackbar(
        "Assessment report notification sent to patient successfully",
        SnackbarConstants.success
      );
    } catch (err) {
      enqueueSnackbar(
        err?.data?.message || "Failed to send notification email",
        SnackbarConstants.error
      );
    } finally {
      setNotifyingFormId(null);
    }
  }

  async function loadAssessmentProgressReport(formId) {
    if (assessmentReportLoading) {
      return;
    }

    setAssessmentReportLoading(true);
    assessmentReportModalRef.current?.openModal();

    try {
      const reportRes = await DoctorFormService.getAssessmentProgressReportData([
        hospitalId,
        patientId,
        formId,
      ]);
      setAssessmentReportData({
        id: formId,
        ...reportRes.data,
      });
    } catch (err) {
      assessmentReportModalRef.current?.closeModal();
      enqueueSnackbar(
        err?.data?.message ||
        "Failed to load assessment progress report. Please try again.",
        SnackbarConstants.error
      );
    } finally {
      setAssessmentReportLoading(false);
    }
  }

  function getLatestSubmittedAssessmentForm() {
    const candidates = formsList.filter(
      (f) =>
        f.assessmentFormTypeEnum?.includes("ASSESSMENT") &&
        !f.assessmentFormTypeEnum?.includes("QEEG") &&
        f.formStatusTypeEnum === "SUBMITTED"
    );
    if (!candidates.length) {
      return null;
    }
    return candidates.reduce((latest, f) =>
      moment(f.createdAt).isAfter(moment(latest.createdAt)) ? f : latest
    );
  }

  function handleViewAllAssessmentProgress() {
    const latest = getLatestSubmittedAssessmentForm();
    if (!latest) {
      enqueueSnackbar(
        "No submitted assessment is available to build a progress report.",
        SnackbarConstants.info
      );
      return;
    }
    loadAssessmentProgressReport(latest.id);
  }

  const getFormTypeName = (formTypeEnum) => {
    const entry = [...DOCTOR_ASSESSMENT_FORM_TYPES, ...DOCTOR_STIM_FORM_TYPES].find(
      (t) => t.formTypeEnum === formTypeEnum
    );
    return entry?.name ?? formTypeEnum ?? "";
  };

  /* API Success Handlers */

  function getFormsHandler(response) {
    let data = response.data;
    let list = [];
    data.map((form, index) => list.push(transformJsonData(form, index + 1)));
    setFormsList(list);
  }

  function addAssessmentFormHandler(response) {
    getForms();
  }

  function createStimFromAssessmentHandler(response) {
    getForms();
    const form = response?.data;
    if (form?.id && form?.assessmentFormTypeEnum) {
      navigateToForm(
        form.assessmentFormTypeEnum,
        hospitalId,
        patientId,
        form.isMigrated ?? false,
        form.id,
        form.formStatusTypeEnum ?? "INCOMPLETE"
      );
    }
  }

  function deleteAssessmentFormHandler(response) {
    getForms();
  }

  function revokeAssessmentFormSubmissionHandler(response) {
    getForms();
  }

  function handleRowClick(row) {
    navigateToForm(
      row.assessmentFormTypeEnum,
      hospitalId,
      patientId,
      row.isMigrated,
      row.id,
      row.formStatusTypeEnum
    );
  }

  const filteredFormsList = formsList
    .filter((item) =>
      activeTab === "STIM"
        ? item.assessmentFormTypeEnum?.includes("STIM")
        : !item.assessmentFormTypeEnum?.includes("STIM")
    )
    .map((item, index) => ({
      ...item,
      index: index + 1,
    }));

  const latestSubmittedAssessmentForm = getLatestSubmittedAssessmentForm();

  return (
    <Paper elevation={0} className="surface-card section-card app-card">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <Stack direction="row" spacing={2} alignItems="center">
          {doesFormsContainAssessments(formsList) && (
            <Button
              type="button"
              className=""
              startIcon={<BarChartIcon />}
              variant="outlined"
              onClick={() =>
                navigate(
                  `/hospital/assessment-analysis?hospitalID=${hospitalId}&patientId=${patientId}`
                )
              }
            >
              Analysis
            </Button>
          )}
          {activeTab === "ASSESSMENT" && latestSubmittedAssessmentForm && (
            <Button
              type="button"
              variant="outlined"
              disabled={assessmentReportLoading}
              onClick={handleViewAllAssessmentProgress}
            >
              View Progress
            </Button>
          )}
        </Stack>
        <Stack
          direction="row"
          justifyContent="right"
          alignItems="center"
          spacing={3}
        >
          {activeTab === "ASSESSMENT" && (
            <ButtonDropdown
              menuItems={DOCTOR_ASSESSMENT_FORM_TYPES}
              onClickHandler={addAssessmentForm}
            />
          )}
          {activeTab === "STIM" && (
            <ButtonDropdown
              menuItems={DOCTOR_STIM_FORM_TYPES}
              onClickHandler={addAssessmentForm}
            />
          )}
        </Stack>
      </div>
      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Assessments" value="ASSESSMENT" />
        <Tab label="Stim" value="STIM" />
      </Tabs>
      {getFormsAPI.status === "pending" ? (
        <div className="loading-state-shell text-center">
          <CircularProgress
            className="text-center"
            sx={{
              my: 2,
            }}
          />
        </div>
      ) : (
        <EnhancedTable
          headings={tableHeadings}
          rowsData={filteredFormsList}
          rowsPerPage={10}
          onRowClick={handleRowClick}
        />
      )}
      <PopUpModal ref={popUpModalRef} modalWidth={350}>
        <AuditFieldsModal data={popUpModalContent} />
      </PopUpModal>
      <PopUpModal ref={deleteAssessmentModalRef} modalWidth={400}>
        <ConfirmActionModal
          content="Do you want to confirm deleting the assessment?"
          confirmActionHandler={() => {
            deleteAssessmentForm(deletingAssessmentId);
            deleteAssessmentModalRef.current.closeModal();
          }}
          onCloseHandler={() => deleteAssessmentModalRef.current.closeModal()}
        />
      </PopUpModal>
      <PopUpModal ref={stimReportPopUpModalRef} modalWidth={"90%"}>
        <StimFormReport
          reportData={stimReportData}
          hospitalId={hospitalId}
          patientId={patientId}
        />
      </PopUpModal>
      <PopUpModal ref={assessmentReportModalRef} modalWidth={"90%"}>
        {assessmentReportLoading ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <AssessmentFormReport
            reportData={assessmentReportData}
            hospitalId={hospitalId}
            patientId={patientId}
          />
        )}
      </PopUpModal>
    </Paper>
  );

  function transformJsonData(form, index) {
    return {
      id: form.id,
      index: index,
      assessmentFormTypeEnum: form.assessmentFormTypeEnum,
      formStatusTypeEnum: form.formStatusTypeEnum,
      isMigrated: form.isMigrated,
      createdAt: form.createdAt,
      form_type: getFormTypeName(form.assessmentFormTypeEnum),
      status: FormUtils.getStatusChip(form.formStatusTypeEnum),
      createdBy: form.createdByUserFullName,
      lastUpdatedBy: form.lastUpdatedByUserFullName,
      actions: (
        <ActionsCell
          form={form}
        />
      ),
      documentLink: true,
    };
  }

  function ActionsCell({ form }) {
    const actions = {
      startItems: [
        {
          component: (
            <Tooltip title="View Information">
              <InfoOutlinedIcon fontSize="inherit" color="custom.light" />
            </Tooltip>
          ),
          onClickHandler: () => {
            setPopUpModalContent({
              createdAt: DateUtils.formatDateTime(form.createdAt),
              lastUpdatedAt: DateUtils.formatDateTime(form.lastUpdatedAt),
              createdBy: form.createdByUserFullName,
              lastUpdatedBy: form.lastUpdatedByUserFullName,
            });
            popUpModalRef.current.openModal();
          },
        },
        {
          component: (
            <Tooltip title="Delete Form">
              <DeleteOutlineIcon fontSize="inherit" color="error" />
            </Tooltip>
          ),
          onClickHandler: () => {
            setDeletingAssessmentId(form.id);
            deleteAssessmentModalRef.current.openModal();
          },
        },
      ],
      menuItems: [],
    };

    if (form.assessmentFormTypeEnum.includes("STIM") && !form.assessmentFormTypeEnum.includes("QEEG")) {
      if (form.formStatusTypeEnum === "SUBMITTED") {
        actions.menuItems.push({
          label: "Home Program",
          onClickHandler: () => getStimFormReportData(form.id),
        });
      }
    }

    if (
      form.assessmentFormTypeEnum.includes("ASSESSMENT") &&
      !form.assessmentFormTypeEnum.includes("QEEG") &&
      form.assessmentFormTypeEnum !== "LENS_NFB_ASSESSMENT"
    ) {
      if (form.formStatusTypeEnum === "SUBMITTED") {
        actions.menuItems.push({
          label: "Download as PDF",
          onClickHandler: () => downloadAssessmentFormAsPdf(form),
        });
        actions.menuItems.push({
          label:
            notifyingFormId === form.id ? "Sending..." : "Share to Patient",
          onClickHandler: () => notifyPatientAboutAssessment(form.id),
          disabled: notifyingFormId === form.id,
        });
        actions.menuItems.push({
          label: "Create Stim",
          onClickHandler: () => createStimFromAssessment(form.id),
        });
      }
    }

    if (form.formStatusTypeEnum === "SUBMITTED") {
      actions.menuItems.push({
        label: "Revoke Submission",
        onClickHandler: () => revokeAssessmentFormSubmission(form.id),
      });
    }

    return <TableRowActionsCell actions={actions} />;
  }
}

function doesFormsContainAssessments(formsList) {
  return formsList.some((item) =>
    item.assessmentFormTypeEnum?.includes("ASSESSMENT")
  );
}

function navigateToForm(
  patientFormTypeEnum,
  hospitalId,
  patientId,
  isMigrated,
  assessmentFormID,
  formStatus
) {
  const viewMode = isMigrated
    ? "read"
    : formStatus === "SUBMITTED"
      ? "read"
      : "write";

  openDoctorAssessmentForm({
    formTypeEnum: patientFormTypeEnum,
    hospitalId,
    patientId,
    assessmentFormId: assessmentFormID,
    isMigrated,
    viewMode,
  });
}

const tableHeadings = [
  {
    id: "id",
    label: "ID",
  },
  {
    id: "sno",
    label: "S.No.",
  },
  {
    id: "form_type",
    label: "Form Type",
  },
  {
    id: "status",
    label: "Status",
  },
  {
    id: "createdBy",
    label: "Created By",
  },
  {
    id: "lastUpdatedBy",
    label: "Last Updated By",
  },
  {
    id: "actions",
    label: "",
  },
];

export default Assessments;